import React, { useCallback } from 'react';
import { Ban, CheckCircle } from 'lucide-react';
import { Button, Palette, useTheme } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import List from 'components/list/list';
import Checkbox from 'components/checkbox';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import { Pond } from 'views/production-systems/types/pond.type';
import { useAccountState } from 'contexts/account/account-context';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import Lock from 'views/production-systems/features/farm-overview/assets/Lock';
import { kgToLbs } from 'views/production-systems/utils/farm-overview';


const getIcon = (status: boolean, color: Palette) => {
  const Icon = status ? Ban : CheckCircle;
  return <Icon color={status ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type SortablePond = Pond & {
  biomass: number;
  status: boolean;
};

interface Props {
  renderPonds: SortablePond[];
  isFetching: boolean;
  setPonds: React.Dispatch<React.SetStateAction<SortablePond[]>>;
  handleSelectedPonds: (value: Pond, checked: boolean) => void;
  isKg: boolean;
}

const PAGE_NAME_ID = 'productionSystems.farmOverview';

const ListPondsList: React.FC<Props> = ({ renderPonds, isFetching, setPonds, handleSelectedPonds, isKg }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { account } = useAccountState();

  const { lockedPonds, releasePonds, selectedPonds } = usePondsState();

  const isPondDisabled = useCallback(
    (pond: Pond) => {
      return lockedPonds?.granted.some((p) => p === pond.pondId) || !!pond.lockCreatedBy;
    },
    [lockedPonds, renderPonds]
  );

  return (
    <List
      sx={{ height: renderPonds.length > 20 ? '50vh' : 'auto' }}
      isLoading={isFetching}
      noResult={{
        show: !renderPonds.length,
        text: intl.formatMessage({ id: 'general.noResults' }),
      }}
      rows={renderPonds}
      sort={setPonds}
      options={[
        { id: 'checked', label: '', autoWidth: true },
        { id: 'pondName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
        { id: 'zoneName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.zone` }) },
        { id: 'status', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }), align: 'center' },
        {
          id: 'currentQty',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.noShrimps` }),
          align: 'right',
        },
        { id: 'currentMw', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.meanWeight` }), align: 'right' },
        {
          id: 'biomass', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.biomass` }, {
            type: isKg ? 'kg' : 'lbs'
          }), align: 'right'
        },
        {
          id: 'activeInactiveDays',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.days` }),
          align: 'right',
          sx: { whiteSpace: 'nowrap' },
        },
      ]}>
      {renderPonds.map((pond, idx) => (
        <ListRow
          key={idx}
          className={isPondDisabled(pond) ? 'Mui-disabled' : ''}
          sx={{ backgroundColor: isPondDisabled(pond) ? `${theme.palette.darkBlue.o25} !important` : '' }}>
          <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
            {isPondDisabled(pond) && account.currentUser?.email === pond.lockCreatedBy ? (
              <Button
                size="small"
                onClick={() => {
                  releasePonds([pond.pondId]);
                }}>
                <FormattedMessage id={`general.release`} />
              </Button>
            ) : (
              <Checkbox
                tabIndex={-1}
                dark
                hiddenLabel
                disabled={isPondDisabled(pond)}
                checkedIcon={isPondDisabled(pond) && <Lock />}
                checked={isPondDisabled(pond) || selectedPonds.includes(pond.pondId)}
                onChange={(_, checked) => handleSelectedPonds(pond, checked)}
              />
            )}
          </ListCell>
          <ListCell>{pond.pondName}</ListCell>
          <ListCell>{pond.zoneName}</ListCell>
          <ListCell align={'center'}>
            {getIcon(!pond.status, theme.palette)}
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber value={pond.currentQty} />
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber maximumFractionDigits={2} value={pond.currentMw} />
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber maximumFractionDigits={2} value={isKg ? pond.biomass : kgToLbs(pond.biomass)} />
          </ListCell>
          <ListCell align={'right'}>{pond.activeInactiveDays}</ListCell>
        </ListRow>
      ))}
    </List>
  );
};

export default ListPondsList;
